import type { ChangeEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { SegmentedControl, type SegmentedControlOption } from "@/components/ui/SegmentedControl";

interface DomainSidebarProps {
  children: ReactNode;
  className?: string;
}

interface DomainTwoColumnLayoutProps {
  children: ReactNode;
  className?: string;
}

export const DomainTwoColumnLayout = ({ children, className }: DomainTwoColumnLayoutProps) => (
  <div className={`app-domain-two-column-layout relative min-w-0 overflow-x-hidden md:grid md:grid-cols-[minmax(18rem,0.48fr)_minmax(0,0.88fr)] md:items-start md:gap-6 md:overflow-visible lg:h-[calc(100vh-7rem)] lg:grid-cols-[minmax(22rem,0.48fr)_minmax(0,0.88fr)] lg:gap-10 xl:gap-14${className ? ` ${className}` : ""}`}>
    {children}
  </div>
);

export const DomainTwoColumnContent = ({ children, className }: DomainTwoColumnLayoutProps) => (
  <section className={`pb-10 md:col-start-2 md:row-start-1 lg:h-full lg:overflow-y-auto lg:overscroll-contain lg:pr-2${className ? ` ${className}` : ""}`}>
    {children}
  </section>
);

export const DomainSidebar = ({ children, className }: DomainSidebarProps) => (
  <aside className={`app-domain-sidebar${className ? ` ${className}` : ""}`}>{children}</aside>
);

interface DomainSidebarHeaderProps {
  greeting: ReactNode;
  title: ReactNode;
  action?: ReactNode;
}

export const DomainSidebarHeader = ({ greeting, title, action }: DomainSidebarHeaderProps) => (
  <header className="app-domain-sidebar-header">
    <h1 className="app-title mb-2 text-[var(--app-text)]">
      <span className="block">{greeting}</span>
      <span className="block break-words">{title}!</span>
    </h1>
    {action ? <div className="mt-5 flex flex-wrap items-center gap-3 md:mt-8">{action}</div> : null}
  </header>
);

interface DomainSidebarPeriodProps<T extends string> {
  label: ReactNode;
  selectedPeriod: T | null;
  onPeriodChange: (period: T) => void;
  options?: ReadonlyArray<SegmentedControlOption<T>>;
  customStart?: string;
  onCustomStartChange?: (value: string) => void;
  customEnd?: string;
  onCustomEndChange?: (value: string) => void;
  startDateLabel?: ReactNode;
  endDateLabel?: ReactNode;
  className?: string;
  children?: ReactNode;
}

export const DomainSidebarPeriod = <T extends string>({
  label,
  selectedPeriod,
  onPeriodChange,
  options,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  startDateLabel,
  endDateLabel,
  className,
  children,
}: DomainSidebarPeriodProps<T>) => {
  const { t, i18n } = useTranslation("common");
  const showCustomRange = selectedPeriod === "custom" && onCustomStartChange && onCustomEndChange;
  const updateStart = (event: ChangeEvent<HTMLInputElement>) => onCustomStartChange?.(event.target.value);
  const updateEnd = (event: ChangeEvent<HTMLInputElement>) => onCustomEndChange?.(event.target.value);
  const resolvedOptions = (options ?? [
    { id: "this-month", label: t("periods.thisMonth") },
    { id: "last-month", label: t("periods.lastMonth") },
    { id: "custom", label: t("periods.custom") },
  ]) as ReadonlyArray<SegmentedControlOption<T>>;

  return (
    <section className={`app-domain-sidebar-section${className ? ` ${className}` : ""}`}>
      <div className="app-domain-sidebar-period-controls">
        <p className="app-domain-sidebar-label">{label}</p>
        <SegmentedControl
          key={i18n.resolvedLanguage}
          value={selectedPeriod}
          onChange={onPeriodChange}
          options={resolvedOptions}
        />
      </div>
      {showCustomRange ? (
        <div className="app-custom-cycle-panel mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="app-domain-sidebar-control-label">
            <span className="app-domain-sidebar-label mb-0">{startDateLabel}</span>
            <input
              type="date"
              value={customStart}
              max={customEnd}
              onChange={updateStart}
              className="app-domain-sidebar-date-input"
            />
          </label>
          <label className="app-domain-sidebar-control-label">
            <span className="app-domain-sidebar-label mb-0">{endDateLabel}</span>
            <input
              type="date"
              value={customEnd}
              min={customStart}
              onChange={updateEnd}
              className="app-domain-sidebar-date-input"
            />
          </label>
        </div>
      ) : null}
      {children}
    </section>
  );
};
